import { Moon, Sun } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { getTheme, setTheme, subscribe } from '../lib/theme'
import { useHint } from './useHint'
import { theme as S } from './strings'
import './ThemeSwitch.css'

/**
 * The theme control — the legacy header's `الوضع` submenu, rebuilt.
 *
 * Two shapes, one setting, and since the `system` state was retired the setting
 * itself is a plain either/or (see `src/lib/theme.js`).
 *
 * `segmented` (the admin header) shows both states side by side. A cycling
 * button has to be pressed to be read; a pair shows which one you are in
 * without touching it, and the admin bar has the width for two.
 *
 * `toggle` is ONE icon showing the theme you would switch TO — a sun while you
 * are in the dark, a moon while you are in the light. The learner panel floats
 * it at the bottom of the page (tone `float`) rather than keeping it in the
 * header: that bar already carries a burger, a language pill, a bell, a launcher
 * and an account, and preferences are not what anyone opened the panel for.
 */
const OPTIONS = [
  { id: 'light', icon: Sun },
  { id: 'dark', icon: Moon },
]

export default function ThemeSwitch({ tone = 'light', variant = 'segmented' }) {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'light')

  if (variant === 'toggle') return <ThemeToggle tone={tone} theme={theme} />

  return (
    <div
      className={`themeswitch themeswitch--${tone}`}
      role="radiogroup"
      aria-label={S.label}
    >
      {OPTIONS.map(({ id, icon: Icon }) => (
        <Option key={id} id={id} Icon={Icon} on={theme === id} />
      ))}
    </div>
  )
}

/** One of the pair — its own component so it can hold a tooltip's state. */
function Option({ id, Icon, on }) {
  const { hint, bubble } = useHint(S[id])

  return (
    <>
      <button
        type="button"
        role="radio"
        aria-checked={on}
        className={`themeswitch__opt${on ? ' is-on' : ''}`}
        onClick={() => setTheme(id)}
        {...hint}
      >
        <Icon size={15} strokeWidth={1.9} aria-hidden="true" />
        <span className="sr-only">{S[id]}</span>
      </button>
      {bubble}
    </>
  )
}

/**
 * The one-icon form.
 *
 * Both glyphs are always in the DOM, stacked on the same spot, and the theme
 * decides which one is up. That is what lets the swap be a MOVE rather than a
 * repaint: the outgoing glyph rotates out and the incoming one rotates in from
 * the other side, so pressing it reads as turning something over. A single
 * conditionally-rendered icon can only pop.
 */
function ThemeToggle({ tone, theme }) {
  const dark = theme === 'dark'
  const next = dark ? 'light' : 'dark'
  const { hint, bubble } = useHint(S[next])

  return (
    <>
      <button
        type="button"
        className={`themetoggle themetoggle--${tone}${dark ? ' is-dark' : ''}`}
        onClick={() => setTheme(next)}
        aria-label={S[next]}
        {...hint}
      >
        <span className="themetoggle__glyphs" aria-hidden="true">
          <Sun className="themetoggle__sun" size={17} strokeWidth={1.9} />
          <Moon className="themetoggle__moon" size={16} strokeWidth={1.9} />
        </span>
      </button>
      {bubble}
    </>
  )
}
